import React, { useState, useCallback, useMemo, useEffect } from 'react';
import '../css/Eventos.css';
import { ReactComponent as Eliminar } from '../assets/icon/eliminar.svg';
import { ReactComponent as Editar } from '../assets/icon/editar.svg';
import { ReactComponent as Agregar } from '../assets/icon/agregarEvento.svg';
import { ReactComponent as Recargar } from '../assets/icon/recargar.svg';
import { useNavigate } from "react-router-dom";
import TablaEventos from '../Eventos/TablaEventos';
import { useEventos, deleteEvento } from '../services/eventos';
import { useToast } from '../contexts/ToastContext';
import BulkDeleteModal from '../Components/BulkDeleteModal';

function Eventos() {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingItems, setDeletingItems] = useState([]);
  const { showError, showSuccess } = useToast();

  const { data: eventos, loading, error, refetch } = useEventos({
    immediate: true
  });

  useEffect(() => {
    if (error) {
      showError(error, 'Error de conexión');
    }
  }, [error, showError]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    
    const itemsToDelete = eventos.filter(e => selectedIds.includes(e.IdEvento));
    setDeletingItems(itemsToDelete);
    setShowDeleteModal(true);
  }, [selectedIds, eventos]);

  const handleConfirmDelete = useCallback(async (id, item) => {
    const result = await deleteEvento(id);
    if (result.success && result.error === 0) {
      return { success: true };
    }
    throw new Error(result.mensaje || 'Error al eliminar');
  }, []);

  const handleDeleteComplete = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedIds([]);
    refetch();
    showSuccess('Evento(s) eliminado(s) correctamente');
  }, [refetch, showSuccess]);

  const handleEditSelected = useCallback(() => {
    if (selectedIds.length === 1) {
      navigate(`/dashboard/eventos/editareventos/${selectedIds[0]}`);
    }
  }, [selectedIds, navigate]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const buttonStates = useMemo(() => {
    const hasNoSelection = selectedIds.length === 0;
    const hasSingleSelection = selectedIds.length === 1;
    const hasMultipleSelection = selectedIds.length > 1;
    const hasData = eventos && eventos.length > 0;

    return {
      nuevo: hasNoSelection && hasData,
      editar: hasSingleSelection && hasData,
      eliminar: (hasSingleSelection || hasMultipleSelection) && hasData
    };
  }, [selectedIds, eventos]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div id="eventos-page" className="bodyEventos">

      {/* TÍTULO */}
      <div id="eventos-titulo" className='tituloEventos'><p>Eventos</p></div>

      {/* CONTENIDO SUPERIOR */}
      <div id="eventos-contenido-superior" className='contenidoSuperiorEventos'>
        <div id="eventos-botones" className='botonesEventos'>
          <button
            id="eventos-btn-agregar"
            className='botonesEdicionEventos'
            onClick={() => navigate("/dashboard/eventos/alta-eventos")}
            disabled={!buttonStates.nuevo}
            title={buttonStates.nuevo ? "Agregar nuevo evento" : "Deshabilitado cuando hay selección"}
          >
            <div className="svgBotonesEventos"><Agregar className="logo-svgBotones" /></div>
            Agregar Evento
          </button>

          <button
            id="eventos-btn-editar"
            className='botonesEdicionEmpresas'
            disabled={!buttonStates.editar}
            onClick={handleEditSelected}
            title={buttonStates.editar ? "Editar evento seleccionado" : "Selecciona un evento para editar"}
          >
            <div className="svgBotonesEventos"><Editar className="logo-svgBotones" /></div>
            Editar Evento
          </button>

          <button 
            id="eventos-btn-eliminar"
            className='botonesEdicionEventos'
            disabled={!buttonStates.eliminar}
            onClick={handleDeleteSelected}
            title={buttonStates.eliminar ? `Eliminar ${selectedIds.length} evento(s)` : "Selecciona eventos para eliminar"}
          >
            <div className="svgBotonesEmpresas"><Eliminar className="logo-svgBotones" /></div>
            Eliminar Evento{selectedIds.length > 1 ? 's' : ''}
          </button>

          <button 
            id="eventos-btn-recargar"
            className='botonesEdicionEventos'
            onClick={handleRefresh}
            disabled={loading}
            title="Recargar eventos"
          >
            <div className="svgBotonesEmpresas">
              <Recargar className={`logo-svgBotones ${loading ? 'rotating' : ''}`} />
            </div>
            Recargar
          </button>
        </div>
      </div>

      {/* AQUÍ SE MOSTRARÁ LA TABLA */}
      <div id="eventos-tabla-container" className="tablaContainerEventos">
        <TablaEventos 
          eventos={eventos}
          loading={loading}
          error={error}
          onRetry={refetch}
          emptyStateNoReload={true}
          pageSize={20}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSelectionChange={(selectedIdsList) => {
            setSelectedIds(selectedIdsList || []);
            if (currentPage !== 1) {
              setCurrentPage(1);
            }
          }}
        />
      </div>

      <BulkDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar Eventos"
        items={deletingItems}
        onDelete={handleConfirmDelete}
        getItemId={(item) => item.IdEvento}
        getItemLabel={(item) => item.Titulo || `Evento #${item.IdEvento}`}
        entityName="evento"
      />

    </div>
  );
}

export default Eventos;
